/* eslint-disable consistent-return */
/**
 * patientNotificationPreferences — PR-9.
 *
 * Per-practice toggles for PATIENT-FACING notifications (currently only
 * the FORM_SUBMISSION_ACK event in two channels: EMAIL and WHATSAPP).
 *
 * Distinct from the existing `PracticeUserNotificationPreference` model
 * which targets STAFF — those rows live behind the super-admin Role
 * Defaults / Per-User Preferences tabs and are NOT mutated by this
 * slice. The two notification streams ship side-by-side and are
 * additive: the patient toggles never touch staff delivery and
 * vice-versa.
 *
 * BE contract (shipped in PR-7):
 *
 *     GET   /api/v1/practices/{practice_pk}/patient-notification-preferences/
 *               -> [{ id, notification_event, notification_type, is_enabled }, ...]
 *     PATCH /api/v1/practices/{practice_pk}/patient-notification-preferences/{id}/
 *               body: { is_enabled: bool }
 *               -> updated row
 *
 *   `is_enabled` is the only mutable field — the (event, channel) tuple
 *   is fixed by the BE seeder so the FE never POSTs/DELETEs rows.
 *
 * State shape:
 *
 *     {
 *       byPractice: {
 *         [practiceId]: {
 *           rows: PatientNotificationPreferenceRow[],
 *           loading: boolean,
 *           saving: { [rowId]: boolean },
 *           error: string | null,
 *           fetchedAt: number | null,
 *         }
 *       }
 *     }
 *
 * Optimistic update strategy (PATCH):
 *   1. flip `is_enabled` in `rows` immediately + mark `saving[id] = true`.
 *   2. on 2xx — reconcile the row from the response payload and clear
 *      `saving[id]`.
 *   3. on 4xx/5xx — revert the row to its pre-flip value, clear
 *      `saving[id]`, and surface an error toast via the shared
 *      `updateError` action so the existing snackbar layer renders it.
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch as appDispatch } from '../store';
import { callApi, callAdminApi } from '../../utils/apiCall';
import { updateError } from './user';

const _emptyEntry = () => ({
    rows: [],
    loading: false,
    saving: {},
    error: null,
    fetchedAt: null,
});

const _emptyRecipientsEntry = () => ({
    data: null,
    loading: false,
    saving: false,
    error: null,
    fetchedAt: null,
});

const _emptyBulkState = () => ({
    inFlight: false,
    dryRunResult: null,
    lastCommit: null,
    error: null,
});

const initialState = {
    byPractice: {},
    // Phase 5 — recipient routing payloads keyed by practice id. Hydrated
    // from the admin notification-recipients endpoint and patched in-place
    // when the operator saves changes from Card 3 of PatientAcknowledgementsTab.
    recipientsByPractice: {},
    // Phase 5 — bulk apply dry-run / commit result. Single global slot —
    // the tab only runs one bulk operation at a time.
    bulk: _emptyBulkState(),
};

const slice = createSlice({
    name: 'patientNotificationPreferences',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { practiceId, loading } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = _emptyEntry();
            }
            state.byPractice[practiceId].loading = !!loading;
            if (loading) {
                state.byPractice[practiceId].error = null;
            }
        },
        setRows(state, action) {
            const { practiceId, rows } = action.payload;
            state.byPractice[practiceId] = {
                ..._emptyEntry(),
                rows: rows || [],
                fetchedAt: Date.now(),
            };
        },
        setError(state, action) {
            const { practiceId, error } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = _emptyEntry();
            }
            state.byPractice[practiceId].error = error;
            state.byPractice[practiceId].loading = false;
        },
        // Optimistic flip of a single row's `is_enabled`. Used before the
        // PATCH fires so the switch animates instantly.
        applyOptimistic(state, action) {
            const { practiceId, id, isEnabled } = action.payload;
            const entry = state.byPractice[practiceId];
            if (!entry) return;
            entry.saving[id] = true;
            // When `isEnabled` is undefined we're marking saving for a
            // non-toggle PATCH (e.g. template change); leave is_enabled
            // alone so the switch doesn't visually flip mid-save.
            if (typeof isEnabled !== 'undefined') {
                entry.rows = entry.rows.map((r) => (
                    r.id === id ? { ...r, is_enabled: !!isEnabled } : r
                ));
            }
        },
        // Reconcile a row from the BE response (handles cases where the
        // server normalises the payload differently from what we sent).
        applyServer(state, action) {
            const { practiceId, row } = action.payload;
            const entry = state.byPractice[practiceId];
            if (!entry || !row) return;
            entry.saving[row.id] = false;
            entry.rows = entry.rows.map((r) => (r.id === row.id ? { ...r, ...row } : r));
        },
        // Revert a previously-optimistic flip when the PATCH fails.
        revertOptimistic(state, action) {
            const { practiceId, id, previousIsEnabled } = action.payload;
            const entry = state.byPractice[practiceId];
            if (!entry) return;
            entry.saving[id] = false;
            entry.rows = entry.rows.map((r) => (
                r.id === id ? { ...r, is_enabled: !!previousIsEnabled } : r
            ));
        },
        // ─── Phase 5: insert a freshly-created row into the cache ───
        // Used after createPatientNotificationPreference returns 2xx so the
        // override matrix re-renders without a re-fetch.
        insertRow(state, action) {
            const { practiceId, row } = action.payload;
            if (!row) return;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = _emptyEntry();
            }
            const entry = state.byPractice[practiceId];
            const exists = entry.rows.some((r) => r.id === row.id);
            entry.rows = exists
                ? entry.rows.map((r) => (r.id === row.id ? { ...r, ...row } : r))
                : entry.rows.concat([row]);
        },
        // ─── Phase 5: recipient routing payload ───
        setRecipientsLoading(state, action) {
            const { practiceId, loading } = action.payload;
            if (!state.recipientsByPractice[practiceId]) {
                state.recipientsByPractice[practiceId] = _emptyRecipientsEntry();
            }
            state.recipientsByPractice[practiceId].loading = !!loading;
            if (loading) {
                state.recipientsByPractice[practiceId].error = null;
            }
        },
        setRecipients(state, action) {
            const { practiceId, data } = action.payload;
            state.recipientsByPractice[practiceId] = {
                ..._emptyRecipientsEntry(),
                data: data || null,
                fetchedAt: Date.now(),
            };
        },
        setRecipientsError(state, action) {
            const { practiceId, error } = action.payload;
            if (!state.recipientsByPractice[practiceId]) {
                state.recipientsByPractice[practiceId] = _emptyRecipientsEntry();
            }
            state.recipientsByPractice[practiceId].error = error;
            state.recipientsByPractice[practiceId].loading = false;
        },
        setRecipientsSaving(state, action) {
            const { practiceId, saving } = action.payload;
            if (!state.recipientsByPractice[practiceId]) {
                state.recipientsByPractice[practiceId] = _emptyRecipientsEntry();
            }
            state.recipientsByPractice[practiceId].saving = !!saving;
        },
        // ─── Phase 5: bulk apply substate ───
        setBulkInFlight(state, action) {
            state.bulk.inFlight = !!action.payload;
            if (action.payload) state.bulk.error = null;
        },
        setBulkDryRun(state, action) {
            state.bulk.dryRunResult = action.payload || null;
            state.bulk.inFlight = false;
        },
        setBulkCommit(state, action) {
            state.bulk.lastCommit = action.payload || null;
            state.bulk.inFlight = false;
        },
        setBulkError(state, action) {
            state.bulk.error = action.payload || null;
            state.bulk.inFlight = false;
        },
        clearBulkResult(state) {
            state.bulk = _emptyBulkState();
        },
    },
});

export const {
    setLoading,
    setRows,
    setError,
    applyOptimistic,
    applyServer,
    revertOptimistic,
    insertRow,
    setRecipientsLoading,
    setRecipients,
    setRecipientsError,
    setRecipientsSaving,
    setBulkInFlight,
    setBulkDryRun,
    setBulkCommit,
    setBulkError,
    clearBulkResult,
} = slice.actions;

export default slice.reducer;

/**
 * GET /api/v1/practices/{practiceId}/patient-notification-preferences/
 *
 * Hydrates `state.patientNotificationPreferences.byPractice[practiceId]`
 * with the seeded rows for the practice. The BE returns an empty list
 * when the practice was created before the patient-notification seeder
 * was rolled out — the UI treats that as "feature not available yet for
 * this practice" via the empty-state Alert.
 *
 * Accepts an optional `dispatchFn` arg for tests; defaults to the
 * app-wide dispatch from the store. Same indirection as
 * `fetchSystemTemplates` in tasksV2DefaultTemplates.js.
 */
export function fetchPatientNotificationPreferences(
    practiceId,
    optsOrDispatch = appDispatch,
) {
    // Backwards-compatible call signature:
    //   fetchPatientNotificationPreferences(id)
    //   fetchPatientNotificationPreferences(id, customDispatch)
    //   fetchPatientNotificationPreferences(id, { formType, dispatchFn })
    const isOptsObject = optsOrDispatch && typeof optsOrDispatch === 'object'
        && (Object.prototype.hasOwnProperty.call(optsOrDispatch, 'formType')
            || Object.prototype.hasOwnProperty.call(optsOrDispatch, 'dispatchFn'));
    const formType = isOptsObject ? optsOrDispatch.formType : undefined;
    const dispatchFn = isOptsObject
        ? (optsOrDispatch.dispatchFn || appDispatch)
        : (optsOrDispatch || appDispatch);
    return async () => {
        if (!practiceId) return null;
        dispatchFn(slice.actions.setLoading({ practiceId, loading: true }));
        try {
            const qs = (formType === null || typeof formType === 'undefined')
                ? ''
                : `?form_type=${encodeURIComponent(formType)}`;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/patient-notification-preferences/${qs}`,
            );
            if (!response?.ok) {
                dispatchFn(slice.actions.setError({
                    practiceId,
                    error: response?.data?.detail
                        || response?.error?.message
                        || `Failed to load patient notification preferences (HTTP ${response?.status || 0})`,
                }));
                return null;
            }
            const { data } = response;
            const rows = Array.isArray(data) ? data : (data?.results || []);
            dispatchFn(slice.actions.setRows({ practiceId, rows }));
            return rows;
        } catch (error) {
            dispatchFn(slice.actions.setError({
                practiceId,
                error: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatchFn(slice.actions.setLoading({ practiceId, loading: false }));
        }
    };
}

/**
 * PATCH a single row's `is_enabled`.
 *
 * Returns `{ ok, row? , error? }` — never throws — so the caller can
 * fire-and-forget from a Switch onChange. The reducer plumbing handles
 * optimistic flip / revert / snackbar internally.
 *
 *   updatePatientNotificationPreference({ practiceId, id, isEnabled })
 */
export function updatePatientNotificationPreference({
    practiceId, id, isEnabled, dispatchFn = appDispatch,
}) {
    return async () => {
        if (!practiceId || !id) return { ok: false };
        // Capture the previous state so we can revert on failure. We read
        // it via a closure over `appDispatch` instead of asking the caller
        // for it — keeps the API surface tiny.
        let previousIsEnabled = null;
        try {
            // eslint-disable-next-line global-require
            const { store } = require('../store');
            const entry = store?.getState?.()?.patientNotificationPreferences?.byPractice?.[practiceId];
            const row = entry?.rows?.find((r) => r.id === id);
            previousIsEnabled = row ? !!row.is_enabled : !isEnabled;
        } catch (_) {
            previousIsEnabled = !isEnabled;
        }

        dispatchFn(slice.actions.applyOptimistic({ practiceId, id, isEnabled }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/patient-notification-preferences/${id}/`,
                'patch',
                { is_enabled: !!isEnabled },
            );
            if (!response?.ok) {
                dispatchFn(slice.actions.revertOptimistic({ practiceId, id, previousIsEnabled }));
                const message = response?.data?.detail
                    || response?.data?.error
                    || `Failed to update preference (HTTP ${response?.status || 0})`;
                dispatchFn(updateError({ message, success: false }));
                return { ok: false, error: message };
            }
            dispatchFn(slice.actions.applyServer({ practiceId, row: response.data }));
            return { ok: true, row: response.data };
        } catch (error) {
            dispatchFn(slice.actions.revertOptimistic({ practiceId, id, previousIsEnabled }));
            const message = error?.message || 'Network error updating preference';
            dispatchFn(updateError({ message, success: false }));
            return { ok: false, error: message };
        }
    };
}

/**
 * PATCH a single row's template FK (email_template_id / whatsapp_template_id).
 *
 * No optimistic reducer — template-name resolution lives on the BE so
 * we wait for the PATCH response and reconcile via `applyServer`. The
 * matrix shows a small "saving…" affordance while the request is in
 * flight. Pass `templateId: null` to clear the template.
 *
 *   updatePatientNotificationPreferenceTemplate({
 *     practiceId, id, channel: 'EMAIL' | 'WHATSAPP', templateId,
 *   })
 */
export function updatePatientNotificationPreferenceTemplate({
    practiceId, id, channel, templateId, dispatchFn = appDispatch,
}) {
    return async () => {
        if (!practiceId || !id || !channel) return { ok: false };
        const key = channel === 'EMAIL' ? 'email_template_id' : 'whatsapp_template_id';
        // Mark saving via the same per-row map the toggle uses so the UI
        // can disable interactions during the round-trip.
        dispatchFn(slice.actions.applyOptimistic({ practiceId, id, isEnabled: undefined }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/patient-notification-preferences/${id}/`,
                'patch',
                { [key]: templateId == null ? null : templateId },
            );
            if (!response?.ok) {
                // Clear the saving flag without flipping is_enabled.
                dispatchFn(slice.actions.applyServer({
                    practiceId,
                    row: { id },
                }));
                const message = response?.data?.detail
                    || response?.data?.[key]?.[0]
                    || response?.data?.error
                    || `Failed to update template (HTTP ${response?.status || 0})`;
                dispatchFn(updateError({ message, success: false }));
                return { ok: false, error: message };
            }
            // Defence-in-depth: the slice's `applyServer` spread-merges
            // (`{...prev, ...row}`), so a BE response that *omits* the
            // nested ``email_template`` / ``whatsapp_template`` object on
            // clear would leave the stale name visible in the chip until
            // the next refetch. Today the BE serialiser (PR-7) emits the
            // nested key as ``null`` on PATCH responses, but we layer the
            // normalisation here so a future BE shape change doesn't
            // silently regress the "Clear template" path. We ONLY null
            // out the side that was just cleared — the other channel's
            // template MUST be preserved.
            const serverRow = response.data || {};
            const normalised = { ...serverRow };
            if (templateId == null) {
                if (channel === 'EMAIL') {
                    normalised.email_template = null;
                    normalised.email_template_id = null;
                } else {
                    normalised.whatsapp_template = null;
                    normalised.whatsapp_template_id = null;
                }
            }
            dispatchFn(slice.actions.applyServer({ practiceId, row: normalised }));
            return { ok: true, row: normalised };
        } catch (error) {
            dispatchFn(slice.actions.applyServer({ practiceId, row: { id } }));
            const message = error?.message || 'Network error updating template';
            dispatchFn(updateError({ message, success: false }));
            return { ok: false, error: message };
        }
    };
}

// ─────────────────────────────────────────────────────────────────────
// Phase 5 — extra thunks for the rebuilt PatientAcknowledgementsTab.
// All keep the existing { ok, ...data | error } return shape and never
// throw so call-sites can fire-and-forget. The slice-level setError /
// updateError dispatches surface failures via the shared snackbar.
// ─────────────────────────────────────────────────────────────────────

/**
 * POST /api/v1/practices/{practiceId}/patient-notification-preferences/
 *
 * Used when the bulk-apply dry-run reports "create" for a
 * (practice, form_type, channel) tuple that has no existing row.
 * Returns { ok, row?, error? }.
 */
export function createPatientNotificationPreference({
    practiceId, payload, dispatchFn = appDispatch,
}) {
    return async () => {
        if (!practiceId || !payload) return { ok: false };
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/patient-notification-preferences/`,
                'post',
                payload,
            );
            if (!response?.ok) {
                const message = response?.data?.detail
                    || response?.data?.error
                    || `Failed to create preference (HTTP ${response?.status || 0})`;
                dispatchFn(updateError({ message, success: false }));
                return { ok: false, status: response?.status, error: message };
            }
            dispatchFn(slice.actions.insertRow({ practiceId, row: response.data }));
            return { ok: true, row: response.data };
        } catch (error) {
            const message = error?.message || 'Network error creating preference';
            dispatchFn(updateError({ message, success: false }));
            return { ok: false, error: message };
        }
    };
}

/**
 * POST /api/v1/admin/patient-ack-bulk/
 *
 * Bulk upsert across selected practices for a single (form_type, channel)
 * tuple. `practicePks: []` means "all practices in caller scope".
 *
 * `dryRun: true` returns a preview shape:
 *   { would_write: [{ practice_id, action, current, next }], errors, scope }
 *
 * `dryRun: false` returns a commit summary: `{ written, skipped, errors }`.
 *
 * Result is also persisted to `state.patientNotificationPreferences.bulk`
 * so the tab can render the preview banner without managing local state.
 */
export function bulkApplyPatientAck({
    practicePks,
    formType,
    notificationType,
    isEnabled,
    emailTemplateId,
    whatsappTemplateId,
    dryRun,
    dispatchFn = appDispatch,
}) {
    return async () => {
        dispatchFn(slice.actions.setBulkInFlight(true));
        const payload = {
            practice_pks: Array.isArray(practicePks) ? practicePks : [],
            form_type: typeof formType === 'undefined' ? null : formType,
            notification_type: notificationType,
            is_enabled: !!isEnabled,
            dry_run: !!dryRun,
        };
        if (notificationType === 'EMAIL') {
            payload.email_template_id = emailTemplateId || null;
        }
        if (notificationType === 'WHATSAPP') {
            payload.whatsapp_template_id = whatsappTemplateId || null;
        }
        try {
            const response = await callAdminApi(
                '/api/v1/admin/patient-ack-bulk/',
                'post',
                payload,
            );
            if (!response?.ok) {
                const message = response?.data?.detail
                    || response?.data?.error
                    || `Bulk apply failed (HTTP ${response?.status || 0})`;
                dispatchFn(slice.actions.setBulkError(message));
                dispatchFn(updateError({ message, success: false }));
                return { ok: false, error: message, data: response?.data };
            }
            if (dryRun) {
                dispatchFn(slice.actions.setBulkDryRun(response.data));
            } else {
                dispatchFn(slice.actions.setBulkCommit(response.data));
            }
            return { ok: true, data: response.data };
        } catch (error) {
            const message = error?.message || 'Network error during bulk apply';
            dispatchFn(slice.actions.setBulkError(message));
            dispatchFn(updateError({ message, success: false }));
            return { ok: false, error: message };
        }
    };
}

/**
 * GET /api/v1/admin/practices/{practiceId}/notification-recipients/
 *
 * Hydrates the recipient-routing payload (existing fields + the four new
 * `form_ack_*` fields added in Phase 3).
 */
export function fetchAckRecipients(practiceId, dispatchFn = appDispatch) {
    return async () => {
        if (!practiceId) return null;
        dispatchFn(slice.actions.setRecipientsLoading({ practiceId, loading: true }));
        try {
            const response = await callAdminApi(
                `/api/v1/admin/practices/${practiceId}/notification-recipients/`,
            );
            if (!response?.ok) {
                const message = response?.data?.detail
                    || response?.error?.message
                    || `Failed to load recipients (HTTP ${response?.status || 0})`;
                dispatchFn(slice.actions.setRecipientsError({ practiceId, error: message }));
                return null;
            }
            dispatchFn(slice.actions.setRecipients({ practiceId, data: response.data }));
            return response.data;
        } catch (error) {
            const message = error?.message || 'Network error loading recipients';
            dispatchFn(slice.actions.setRecipientsError({ practiceId, error: message }));
            return null;
        } finally {
            dispatchFn(slice.actions.setRecipientsLoading({ practiceId, loading: false }));
        }
    };
}

/**
 * PATCH /api/v1/admin/practices/{practiceId}/notification-recipients/
 *
 * Partial update of the recipient-routing payload. Returns the fresh
 * server payload so the caller can re-render. Failures bubble via the
 * shared snackbar (`updateError`).
 */
export function updateAckRecipients({
    practiceId, payload, dispatchFn = appDispatch,
}) {
    return async () => {
        if (!practiceId || !payload) return { ok: false };
        dispatchFn(slice.actions.setRecipientsSaving({ practiceId, saving: true }));
        try {
            const response = await callAdminApi(
                `/api/v1/admin/practices/${practiceId}/notification-recipients/`,
                'patch',
                payload,
            );
            if (!response?.ok) {
                const message = response?.data?.detail
                    || response?.data?.error
                    || `Failed to update recipients (HTTP ${response?.status || 0})`;
                dispatchFn(updateError({ message, success: false }));
                return { ok: false, error: message };
            }
            dispatchFn(slice.actions.setRecipients({ practiceId, data: response.data }));
            return { ok: true, data: response.data };
        } catch (error) {
            const message = error?.message || 'Network error updating recipients';
            dispatchFn(updateError({ message, success: false }));
            return { ok: false, error: message };
        } finally {
            dispatchFn(slice.actions.setRecipientsSaving({ practiceId, saving: false }));
        }
    };
}
